import type { ReactNode } from "react";
import {
  planDistanceM,
  type PlanPoint,
  type PlanRoom,
  type PlanSegment,
  type PlanSnapKind,
} from "@sts/geometry";
import { formatArea, formatLength } from "@sts/scan-viewer";
import { toView, type PlanFit } from "./plan-view.js";
import type { PlanDimension } from "./plan-annotations.js";

/**
 * The marks a floor plan is drawn with — floor, wall, opening, dimension,
 * scale — each a small pure function of plan metres and a `PlanFit`.
 *
 * Split out of `FloorPlan.tsx`, which owns the state, the pointer handling and
 * the layout; nothing in here holds state or knows about storage. Every length
 * printed here is `formatLength` over either `PlanSegment.lengthM` (which is
 * `ScanElement.widthM` verbatim — the field the tables print) or the straight
 * distance between two plan points, so the drawing cannot quote a figure the
 * tables disagree with.
 *
 * Colours are all CSS classes over the portal's design tokens (the
 * `.floor-plan*` block in `styles.css`); geometry and pointer behaviour are set
 * here, inline, so the drawing works whatever stylesheet is around it.
 */

type Vec = { readonly x: number; readonly y: number };

/** Clicks pass straight through a mark that is not currently interactive. */
const INERT = { pointerEvents: "none" } as const;

/** Offset of an automatic dimension line from the wall face, in view px. */
const DIM_OFFSET = 24;
const WITNESS_GAP = 6;
const TICK = 4.5;
/** An invisible fat stroke over each mark: a thin line is a hard click target. */
export const HIT_UNITS = 18;

/**
 * How thick to draw a wall, in view px.
 *
 * RoomPlan reports walls as planes — no thickness — so this is a drawing
 * convention, not a measurement: roughly a 120 mm wall at the current scale,
 * clamped so it is neither a hairline on a whole-house plan nor a slab when
 * zoomed in. It never feeds a number.
 */
export function wallThickness(fit: PlanFit): number {
  return Math.max(5, Math.min(14, 0.12 * fit.pixelsPerMetre));
}

/**
 * How thick to draw one wall: to scale when the designer has given it a
 * thickness in the plan's edit layer (`PlanSegment.thicknessM`), otherwise
 * the drawing convention above.
 */
export function segmentThickness(segment: Pick<PlanSegment, "thicknessM"> | undefined, fit: PlanFit): number {
  return segment?.thicknessM !== undefined
    ? Math.max(2, segment.thicknessM * fit.pixelsPerMetre)
    : wallThickness(fit);
}

// --------------------------------------------------------------- floors ----

export function FloorShapes({
  room,
  fit,
  selectedId,
  interactive,
  onPick,
}: {
  readonly room: PlanRoom;
  readonly fit: PlanFit;
  readonly selectedId: string | undefined;
  readonly interactive: boolean;
  readonly onPick: (id: string) => void;
}): ReactNode {
  return (
    <>
      {room.outlines.map((outline, index) => {
        const id = room.floorIds[index];
        const selected = id !== undefined && id === selectedId;
        return (
          <path
            key={id ?? index}
            className={selected ? "floor-plan__floor is-selected" : "floor-plan__floor"}
            data-element-id={id}
            d={`${outline
              .map((point, i) => {
                const p = toView(point, fit);
                return `${i === 0 ? "M" : "L"} ${round(p.x)},${round(p.y)}`;
              })
              .join(" ")} Z`}
            style={interactive && id ? undefined : INERT}
            onClick={
              interactive && id
                ? (event) => {
                    event.stopPropagation();
                    onPick(id);
                  }
                : undefined
            }
          />
        );
      })}
    </>
  );
}

export function RoomLabel({
  room,
  fit,
}: {
  readonly room: PlanRoom;
  readonly fit: PlanFit;
}): ReactNode {
  const centre = toView(room.centre, fit);
  const hasArea = typeof room.floorAreaM2 === "number";
  return (
    // Inert, so a click on the room's name lands on the floor beneath it and
    // selects that floor, rather than falling through to "deselect".
    <g className="floor-plan__room-label" style={INERT}>
      <text x={round(centre.x)} y={round(centre.y - (hasArea ? 8 : 0))}>
        {room.label}
      </text>
      {hasArea ? (
        <text
          className="floor-plan__room-area"
          x={round(centre.x)}
          y={round(centre.y + 10)}
        >
          {formatArea(room.floorAreaM2 as number, "m")}
        </text>
      ) : null}
    </g>
  );
}

// ---------------------------------------------------------------- walls ----

/** On-screen length (view units) a wall needs before its number is drawn. */
export const WALL_NUMBER_MIN_PX = 44;

/** Inside one of these closed loops, by the even-odd rule. */
function insideOutlines(outlines: readonly (readonly PlanPoint[])[], p: PlanPoint): boolean {
  return outlines.some((poly) => {
    let hit = false;
    for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
      const a = poly[i] as PlanPoint;
      const b = poly[j] as PlanPoint;
      if (a.y > p.y !== b.y > p.y && p.x < ((b.x - a.x) * (p.y - a.y)) / (b.y - a.y) + a.x) hit = !hit;
    }
    return hit;
  });
}

/**
 * A wall's number in its room — the "Wall 3" the tables, the checks and the
 * heat loss call it — as a small badge just inside the room by the wall's
 * middle. A wall between two rooms gets one on each side, because it has a
 * number in each: "Wall 5" of one room can be "Wall 2" of the other.
 *
 * A wall too short on screen to hold a badge beside it has none — until it is
 * selected, or the plan is zoomed (or made full screen) far enough to fit one:
 * a cluster of badges on a cupboard's walls said nothing legible and hid the
 * dimensions beside them.
 */
/**
 * A short, stable way to say which room a wall belongs to.
 *
 * "Room 3" gives "3", which matches what the checks and the tables call it.
 * A named room gives its first two letters. Null when there is nothing
 * useful to say, in which case the badge stays as it was.
 */
function roomTagOf(room: PlanRoom): string | null {
  const numbered = /^Room\s+(\d+)$/i.exec(room.label.trim());
  if (numbered) return numbered[1]!;
  const letters = room.label.trim().replace(/[^\p{L}\p{N}]/gu, "");
  return letters === "" ? null : letters.slice(0, 2);
}

export function WallNumber({
  segment,
  room,
  fit,
  selected = false,
}: {
  readonly segment: PlanSegment;
  readonly room: PlanRoom;
  readonly fit: PlanFit;
  readonly selected?: boolean;
}): ReactNode {
  const dx = segment.end.x - segment.start.x;
  const dy = segment.end.y - segment.start.y;
  const len = Math.hypot(dx, dy);
  if (len < 1e-6) return null;
  const mid = { x: (segment.start.x + segment.end.x) / 2, y: (segment.start.y + segment.end.y) / 2 };
  // Which side of the wall the room is on: the side a short step lands inside
  // its floor — or, with no floor seen, the side its centre is on.
  let normal = { x: -dy / len, y: dx / len };
  const probe = { x: mid.x + normal.x * 0.3, y: mid.y + normal.y * 0.3 };
  const inward =
    room.outlines.length > 0
      ? insideOutlines(room.outlines, probe)
      : (room.centre.x - mid.x) * normal.x + (room.centre.y - mid.y) * normal.y > 0;
  if (!inward) normal = { x: -normal.x, y: -normal.y };
  // A fixed distance on screen, whatever the zoom: measured in view units.
  const m = toView(mid, fit);
  const step = toView({ x: mid.x + normal.x, y: mid.y + normal.y }, fit);
  const unit = Math.hypot(step.x - m.x, step.y - m.y) || 1;
  if (!selected && len * unit < WALL_NUMBER_MIN_PX) return null;
  const p = { x: m.x + ((step.x - m.x) / unit) * 15, y: m.y + ((step.y - m.y) / unit) * 15 };
  // THE ROOM TRAVELS WITH THE NUMBER, and that is the whole point of this
  // badge changing. Walls are numbered 1..N WITHIN each room, because MCS
  // heat loss is worked out room by room and every table, check and installer
  // pack says "Wall 7 in Room 3". The badge used to strip the room off and
  // draw a bare "7", so every room on the plan had a 1, a 2, a 3 — and a
  // reader going down a column of checks could not tell which "Wall 1" was
  // being complained about (2026-10-08).
  //
  // So it reads "3·7": the room, then the wall. The room's own number when it
  // has one ("Room 3"), and its first letters when somebody has named it
  // ("Ki·7" for a Kitchen) — on the plan the room's full name is printed
  // across its floor anyway, so two letters is enough to tie the two
  // together. Nothing stored changes: this is how the number is DRAWN.
  const wallNumber = /^Wall (\d+)$/.exec(segment.label)?.[1] ?? segment.label;
  const roomTag = roomTagOf(room);
  const number = roomTag === null ? wallNumber : `${roomTag}\u00b7${wallNumber}`;
  return (
    <g
      className={selected ? "floor-plan__wall-number is-selected" : "floor-plan__wall-number"}
      data-wall-number-for={segment.id}
      style={INERT}
    >
      <title>{`${segment.label}, ${room.label}`}</title>
      {/* Wide enough for what is in it: "3·7" is four characters where a
          bare "7" was one, and a circle sized for one digit clipped it. */}
      {number.length > 3 ? (
        <rect
          x={round(p.x) - (number.length * 4 + 6)}
          y={round(p.y) - 9}
          width={number.length * 8 + 12}
          height={18}
          rx={9}
        />
      ) : (
        <circle cx={round(p.x)} cy={round(p.y)} r={number.length > 2 ? 11 : 8} />
      )}
      <text x={round(p.x)} y={round(p.y)}>
        {number}
      </text>
    </g>
  );
}

/** A wall: a thick stroke, plus an invisible fatter one over it for the click. */
export function Wall({
  segment,
  fit,
  selected,
  interactive,
  onPick,
}: {
  readonly segment: PlanSegment;
  readonly fit: PlanFit;
  readonly selected: boolean;
  readonly interactive: boolean;
  readonly onPick: (id: string) => void;
}): ReactNode {
  const a = toView(segment.start, fit);
  const b = toView(segment.end, fit);
  return (
    <g
      className={selected ? "floor-plan__wall is-selected" : "floor-plan__wall"}
      data-element-id={segment.id}
    >
      <line
        className="floor-plan__wall-body"
        x1={round(a.x)}
        y1={round(a.y)}
        x2={round(b.x)}
        y2={round(b.y)}
        strokeWidth={segmentThickness(segment, fit)}
        // Square caps extend each end by half the thickness, which is exactly
        // what closes a corner where two wall centrelines meet.
        strokeLinecap="square"
        style={INERT}
      />
      <HitLine a={a} b={b} interactive={interactive} onPick={() => onPick(segment.id)} />
    </g>
  );
}

/**
 * A window, door or opening, drawn as a break in the wall run.
 *
 * The wall is cut first — a stroke in the paper colour over the wall body —
 * then the opening's own mark goes in the gap: glazing lines for a window, a
 * leaf and swing for a door, bare jambs for an opening. That is the convention
 * on every architectural plan, and it is what makes a plan readable at a
 * glance rather than a set of coloured bars.
 *
 * The door's swing is a convention too. RoomPlan does not record which way a
 * door opens, so the leaf is drawn into the room from the segment's start and
 * the arc is dashed; it says "door here", not "hinged on this side".
 */
export function Opening({
  segment,
  room,
  fit,
  selected,
  interactive,
  onPick,
}: {
  readonly segment: PlanSegment;
  readonly room: PlanRoom;
  readonly fit: PlanFit;
  readonly selected: boolean;
  readonly interactive: boolean;
  readonly onPick: (id: string) => void;
}): ReactNode {
  const a = toView(segment.start, fit);
  const b = toView(segment.end, fit);
  const length = Math.hypot(b.x - a.x, b.y - a.y);
  if (length < 0.5) return null;
  const u = { x: (b.x - a.x) / length, y: (b.y - a.y) / length };
  const inward = inwardNormal(u, midpoint(a, b), toView(room.centre, fit));
  const thickness = segmentThickness(
    room.segments.find((s) => s.id === segment.parentId),
    fit,
  );
  const half = thickness / 2;

  const kind =
    segment.kind === "window" ? "window" : segment.kind === "door" ? "door" : "gap";

  const jamb = (p: Vec): string =>
    `M ${round(p.x + inward.x * half)},${round(p.y + inward.y * half)} L ${round(p.x - inward.x * half)},${round(p.y - inward.y * half)}`;

  let mark: ReactNode;
  if (kind === "window") {
    // Two panes of glazing, a third of the wall thickness either side of the
    // centreline, closed by the jambs.
    const g = half * 0.4;
    mark = (
      <path
        className="floor-plan__opening-mark"
        fill="none"
        d={[
          `M ${round(a.x + inward.x * g)},${round(a.y + inward.y * g)} L ${round(b.x + inward.x * g)},${round(b.y + inward.y * g)}`,
          `M ${round(a.x - inward.x * g)},${round(a.y - inward.y * g)} L ${round(b.x - inward.x * g)},${round(b.y - inward.y * g)}`,
          jamb(a),
          jamb(b),
        ].join(" ")}
      />
    );
  } else if (kind === "door") {
    // The leaf is as long as the opening is wide — that is what a door is —
    // swung 90° into the room from the hinge.
    const hinge = a;
    const leafEnd = { x: hinge.x + inward.x * length, y: hinge.y + inward.y * length };
    const sweep = inward.x * u.y - inward.y * u.x > 0 ? 1 : 0;
    mark = (
      <>
        <path
          className="floor-plan__opening-mark"
          fill="none"
          d={`${jamb(a)} ${jamb(b)} M ${round(hinge.x)},${round(hinge.y)} L ${round(leafEnd.x)},${round(leafEnd.y)}`}
        />
        <path
          className="floor-plan__door-swing"
          fill="none"
          d={`M ${round(leafEnd.x)},${round(leafEnd.y)} A ${round(length)},${round(length)} 0 0 ${sweep} ${round(b.x)},${round(b.y)}`}
        />
      </>
    );
  } else {
    mark = (
      <path className="floor-plan__opening-mark" fill="none" d={`${jamb(a)} ${jamb(b)}`} />
    );
  }

  return (
    <g
      className={`floor-plan__opening floor-plan__opening--${kind}${selected ? " is-selected" : ""}`}
      data-element-id={segment.id}
    >
      <g style={INERT}>
        <line
          className="floor-plan__opening-cut"
          x1={round(a.x)}
          y1={round(a.y)}
          x2={round(b.x)}
          y2={round(b.y)}
          strokeWidth={thickness + 2}
          strokeLinecap="butt"
        />
        {mark}
      </g>
      <HitLine a={a} b={b} interactive={interactive} onPick={() => onPick(segment.id)} />
    </g>
  );
}

function HitLine({
  a,
  b,
  interactive,
  onPick,
}: {
  readonly a: Vec;
  readonly b: Vec;
  readonly interactive: boolean;
  readonly onPick: () => void;
}): ReactNode {
  return (
    <line
      className="floor-plan__hit"
      x1={round(a.x)}
      y1={round(a.y)}
      x2={round(b.x)}
      y2={round(b.y)}
      stroke="transparent"
      strokeWidth={HIT_UNITS}
      style={interactive ? { cursor: "pointer" } : INERT}
      onClick={
        interactive
          ? (event) => {
              event.stopPropagation();
              onPick();
            }
          : undefined
      }
    />
  );
}

// ------------------------------------------------------------ dimensions ----

/**
 * The measured length of one wall, on a dimension line outside the room.
 *
 * Offset outward from the room's own centroid so the figure never sits on the
 * floor it describes, and turned to run along the wall — folded back upright
 * where it would otherwise read upside down.
 */
export function AutoDimension({
  segment,
  room,
  fit,
  selected,
}: {
  readonly segment: PlanSegment;
  readonly room: PlanRoom;
  readonly fit: PlanFit;
  /** Its wall is the selected element: the figure lights with it. */
  readonly selected: boolean;
}): ReactNode {
  const a = toView(segment.start, fit);
  const b = toView(segment.end, fit);
  const length = Math.hypot(b.x - a.x, b.y - a.y);
  // Too short to carry its own figure legibly: the table has it.
  if (length < 28) return null;
  const u = { x: (b.x - a.x) / length, y: (b.y - a.y) / length };
  const outward = outwardNormal(u, midpoint(a, b), toView(room.centre, fit));
  const at = (p: Vec, by: number): Vec => ({
    x: p.x + outward.x * by,
    y: p.y + outward.y * by,
  });
  const offset = DIM_OFFSET + wallThickness(fit) / 2;
  const da = at(a, offset);
  const db = at(b, offset);
  const mid = midpoint(da, db);
  const angle = uprightAngle(u);

  return (
    <g
      className={`floor-plan__dim floor-plan__dim--auto${selected ? " is-selected" : ""}`}
      data-dimension-for={segment.id}
      style={INERT}
    >
      <Segment className="floor-plan__witness" a={at(a, WITNESS_GAP)} b={at(a, offset + 5)} />
      <Segment className="floor-plan__witness" a={at(b, WITNESS_GAP)} b={at(b, offset + 5)} />
      <Segment className="floor-plan__dim-line" a={da} b={db} />
      <SlashTick at={da} along={u} />
      <SlashTick at={db} along={u} />
      <text
        className="floor-plan__dim-text"
        x={round(mid.x)}
        y={round(mid.y)}
        // Centred on the line; the paper-coloured halo in the stylesheet
        // breaks the line behind the figure, as on a drawn plan.
        transform={`rotate(${round(angle)},${round(mid.x)},${round(mid.y)})`}
      >
        {formatLength(segment.lengthM, "m")}
      </text>
    </g>
  );
}

/** Roughly how wide a label renders at the dimension pill's font size. */
function pillWidth(text: string): number {
  return Math.max(40, text.length * 7 + 16);
}

/** A box, in view px, that a dimension's figure should not be drawn over. */
export interface KeepClear {
  readonly x: number;
  readonly y: number;
  readonly halfWidth: number;
  readonly halfHeight: number;
}

/** The screen box a room's name and area occupy — what a pill must not hide. */
export function roomLabelBox(room: PlanRoom, fit: PlanFit): KeepClear {
  const centre = toView(room.centre, fit);
  return {
    x: centre.x,
    y: centre.y,
    halfWidth: Math.max(room.label.length * 4.6, 36),
    // Name 8px above centre, area 10px below, each ~15px tall.
    halfHeight: 18,
  };
}

/**
 * Where the pill goes: the midpoint, unless that would sit on a room's name —
 * then the nearest clear point along the line, and failing that, beside it.
 * A diagonal drawn corner to corner passes straight through the middle of the
 * room, which is exactly where the name is.
 *
 * The test is done in the line's own frame (along `u`, across `n`), because
 * the pill is rotated with the line and an axis-aligned box around a rotated
 * pill is so much bigger than the pill that nothing ever looks clear.
 */
function pillCentre(
  a: Vec,
  b: Vec,
  u: Vec,
  length: number,
  halfPill: number,
  keepClear: readonly KeepClear[],
): Vec {
  const n = { x: -u.y, y: u.x };
  const mid = midpoint(a, b);
  const HALF_PILL_HEIGHT = 10;
  const GAP = 4;
  const clashes = (p: Vec): boolean =>
    keepClear.some((box) => {
      const dx = box.x - p.x;
      const dy = box.y - p.y;
      const along = Math.abs(dx * u.x + dy * u.y);
      const across = Math.abs(dx * n.x + dy * n.y);
      const boxAlong = box.halfWidth * Math.abs(u.x) + box.halfHeight * Math.abs(u.y);
      const boxAcross = box.halfWidth * Math.abs(u.y) + box.halfHeight * Math.abs(u.x);
      return along < halfPill + boxAlong + GAP && across < HALF_PILL_HEIGHT + boxAcross + GAP;
    });
  if (!clashes(mid)) return mid;
  const reach = length / 2 - halfPill - 6;
  for (let shift = 8; shift <= reach; shift += 8) {
    for (const sign of [1, -1]) {
      const p = { x: mid.x + u.x * shift * sign, y: mid.y + u.y * shift * sign };
      if (!clashes(p)) return p;
    }
  }
  // Nothing clear on the line: set the figure beside it instead.
  for (let off = 16; off <= 120; off += 8) {
    for (const sign of [1, -1]) {
      const p = { x: mid.x + n.x * off * sign, y: mid.y + n.y * off * sign };
      if (!clashes(p)) return p;
    }
  }
  return mid;
}

/** Where a manual dimension's figure is drawn, in view px. */
export interface PillPlacement {
  readonly x: number;
  readonly y: number;
  readonly width: number;
  readonly angle: number;
}

function captionOf(dimension: Pick<PlanDimension, "start" | "end" | "label">): string {
  const reading = formatLength(planDistanceM(dimension.start, dimension.end), "m");
  return dimension.label ? `${dimension.label} · ${reading}` : reading;
}

/**
 * Places one dimension's pill clear of `keepClear` — the rooms' names, and
 * the pills already placed, so two dimensions crossing the same spot do not
 * print their figures on top of each other. `undefined` for a dimension too
 * short on screen to draw.
 */
export function placePill(
  dimension: Pick<PlanDimension, "start" | "end" | "label">,
  fit: PlanFit,
  keepClear: readonly KeepClear[],
): PillPlacement | undefined {
  const a = toView(dimension.start, fit);
  const b = toView(dimension.end, fit);
  const length = Math.hypot(b.x - a.x, b.y - a.y);
  if (length < 0.5) return undefined;
  const u = { x: (b.x - a.x) / length, y: (b.y - a.y) / length };
  const width = pillWidth(captionOf(dimension));
  const at = pillCentre(a, b, u, length, width / 2, keepClear);
  return { x: at.x, y: at.y, width, angle: uprightAngle(u) };
}

/** A placed pill (and its ×) as a box later pills should keep clear of. */
export function pillBox(pill: PillPlacement): KeepClear {
  const r = (pill.angle * Math.PI) / 180;
  const c = Math.abs(Math.cos(r));
  const sn = Math.abs(Math.sin(r));
  const half = pill.width / 2 + 24;
  return { x: pill.x, y: pill.y, halfWidth: half * c + 10 * sn, halfHeight: half * sn + 10 * c };
}

/**
 * A dimension the surveyor drew.
 *
 * In the markup colour, with square end bars and its figure in a filled pill,
 * so it never passes for a measured figure: a scan-measured length and a
 * hand-placed one carry different weight, and a plan that renders them the
 * same invites somebody to build off the wrong one. The figure is the straight
 * distance between the two plan points — the same metres the walls are drawn
 * in, so there is no conversion in between that could drift.
 */
export function ManualDimension({
  dimension,
  fit,
  selected = false,
  pendingBand = false,
  interactive = false,
  onPick,
  onRemove,
  keepClear = [],
  placement,
}: {
  readonly dimension: Pick<PlanDimension, "id" | "start" | "end" | "label">;
  readonly fit: PlanFit;
  readonly selected?: boolean;
  readonly pendingBand?: boolean;
  readonly interactive?: boolean;
  readonly onPick?: (id: string) => void;
  readonly onRemove?: (id: string) => void;
  /** Boxes the figure should not cover — the rooms' names. */
  readonly keepClear?: readonly KeepClear[];
  /** Where the figure goes, when the caller has already placed it. */
  readonly placement?: PillPlacement;
}): ReactNode {
  const a = toView(dimension.start, fit);
  const b = toView(dimension.end, fit);
  const length = Math.hypot(b.x - a.x, b.y - a.y);
  if (length < 0.5) return null;
  const u = { x: (b.x - a.x) / length, y: (b.y - a.y) / length };
  const n = { x: -u.y, y: u.x };
  const caption = captionOf(dimension);
  const pill = placement ?? placePill(dimension, fit, keepClear);
  if (!pill) return null;
  const { angle, width: w } = pill;
  const mid = { x: pill.x, y: pill.y };
  const bar = (p: Vec): ReactNode => (
    <Segment
      className="floor-plan__dim-end"
      a={{ x: p.x + n.x * 6, y: p.y + n.y * 6 }}
      b={{ x: p.x - n.x * 6, y: p.y - n.y * 6 }}
    />
  );

  return (
    <g
      className={[
        "floor-plan__dim",
        "floor-plan__dim--manual",
        selected ? "is-selected" : "",
        pendingBand ? "is-pending" : "",
      ]
        .filter(Boolean)
        .join(" ")}
      data-dimension-id={pendingBand ? undefined : dimension.id}
      style={interactive ? undefined : INERT}
    >
      <Segment className="floor-plan__dim-line" a={a} b={b} />
      {bar(a)}
      {bar(b)}
      {interactive ? (
        <line
          className="floor-plan__hit"
          x1={round(a.x)}
          y1={round(a.y)}
          x2={round(b.x)}
          y2={round(b.y)}
          stroke="transparent"
          strokeWidth={HIT_UNITS}
          style={{ cursor: "pointer" }}
          onClick={(event) => {
            event.stopPropagation();
            onPick?.(dimension.id);
          }}
        />
      ) : null}
      <g transform={`rotate(${round(angle)},${round(mid.x)},${round(mid.y)})`}>
        <rect
          className="floor-plan__pill"
          x={round(mid.x - w / 2)}
          y={round(mid.y - 10)}
          width={round(w)}
          height={20}
          rx={10}
          style={interactive ? { cursor: "pointer" } : undefined}
          onClick={
            interactive
              ? (event) => {
                  event.stopPropagation();
                  onPick?.(dimension.id);
                }
              : undefined
          }
        />
        <text className="floor-plan__pill-text" x={round(mid.x)} y={round(mid.y)} style={INERT}>
          {caption}
        </text>
        {interactive && !pendingBand && onRemove ? (
          // The × — visible on hover and whenever the dimension is selected;
          // absent for someone who may not remove one.
          // Pointer-only; the keyboard path to the same removal is Delete on
          // the selected dimension, or the Remove button in the panel.
          <g
            className="floor-plan__dim-remove"
            data-testid="plan-dimension-remove"
            onClick={(event) => {
              event.stopPropagation();
              onRemove?.(dimension.id);
            }}
          >
            <title>Remove this dimension</title>
            {/* Counter-rotated, so it reads as × on a diagonal, not +. */}
            <g transform={`rotate(${round(-angle)},${round(mid.x + w / 2 + 13)},${round(mid.y)})`}>
              <circle cx={round(mid.x + w / 2 + 13)} cy={round(mid.y)} r={9} />
              <path
                d={`M ${round(mid.x + w / 2 + 9.5)},${round(mid.y - 3.5)} l 7,7 M ${round(mid.x + w / 2 + 16.5)},${round(mid.y - 3.5)} l -7,7`}
              />
            </g>
          </g>
        ) : null}
      </g>
    </g>
  );
}

/** Where the next click will land, and why — corner, wall, or open floor. */
export function SnapMarker({
  point,
  kind,
  fit,
}: {
  readonly point: PlanPoint;
  readonly kind: PlanSnapKind;
  readonly fit: PlanFit;
}): ReactNode {
  const p = toView(point, fit);
  return (
    <g className={`floor-plan__snap floor-plan__snap--${kind}`} style={INERT}>
      {kind === "corner" ? (
        <circle cx={round(p.x)} cy={round(p.y)} r={7} />
      ) : kind === "wall" ? (
        <rect x={round(p.x - 5)} y={round(p.y - 5)} width={10} height={10} transform={`rotate(45,${round(p.x)},${round(p.y)})`} />
      ) : (
        <path d={`M ${round(p.x - 7)},${round(p.y)} h 14 M ${round(p.x)},${round(p.y - 7)} v 14`} />
      )}
    </g>
  );
}

function Segment({
  className,
  a,
  b,
}: {
  readonly className: string;
  readonly a: Vec;
  readonly b: Vec;
}): ReactNode {
  return (
    <line
      className={className}
      x1={round(a.x)}
      y1={round(a.y)}
      x2={round(b.x)}
      y2={round(b.y)}
    />
  );
}

/** The 45° slash that closes each end of an architectural dimension line. */
function SlashTick({ at, along }: { readonly at: Vec; readonly along: Vec }): ReactNode {
  const k = Math.SQRT1_2 * TICK;
  const d = { x: (along.x - along.y) * k, y: (along.x + along.y) * k };
  return (
    <Segment
      className="floor-plan__tick"
      a={{ x: at.x - d.x, y: at.y - d.y }}
      b={{ x: at.x + d.x, y: at.y + d.y }}
    />
  );
}

// ------------------------------------------------------------ scale bar ----

/** A drawn rule, because a plan without a scale bar is a sketch. */
export function ScaleBar({
  fit,
  metres,
  right,
  bottom,
}: {
  readonly fit: PlanFit;
  readonly metres: number;
  /** Where the bar's right-hand end sits, in view px. */
  readonly right: number;
  readonly bottom: number;
}): ReactNode {
  const width = metres * fit.pixelsPerMetre;
  const x1 = round(right - width);
  const x2 = round(right);
  const y = round(bottom);
  const half = round(x1 + width / 2);
  return (
    <g className="floor-plan__scale" style={INERT} data-testid="plan-scale-bar">
      {/* Alternating half-bars, as on a printed plan: the eye reads the half
          as readily as the whole. */}
      <rect className="floor-plan__scale-fill" x={x1} y={y - 3} width={round(width / 2)} height={6} />
      <rect className="floor-plan__scale-open" x={half} y={y - 3} width={round(width / 2)} height={6} />
      <text className="floor-plan__scale-text" x={x1} y={y - 10}>
        0
      </text>
      <text className="floor-plan__scale-text" x={x2} y={y - 10} textAnchor="end">
        {formatLength(metres, "m")}
      </text>
    </g>
  );
}

/**
 * Which way the drawing's axes run: X to the right along the front of the
 * building, Y up the page away from it, Z out of the page (up) — the same
 * X, Y, Z the 3D view shows. Drawn by the scale bar, never over the plan.
 */
export function PlanAxes({ x, y }: { readonly x: number; readonly y: number }): ReactNode {
  const L = 30;
  const head = (tx: number, ty: number, dx: number, dy: number): string => {
    const px = -dy;
    const py = dx;
    return `M ${round(tx)},${round(ty)} L ${round(tx - dx * 6 + px * 3.5)},${round(ty - dy * 6 + py * 3.5)} L ${round(tx - dx * 6 - px * 3.5)},${round(ty - dy * 6 - py * 3.5)} Z`;
  };
  return (
    <g className="floor-plan__axes" style={INERT} data-testid="plan-axes" aria-hidden="true">
      <line className="floor-plan__axis floor-plan__axis--x" x1={round(x)} y1={round(y)} x2={round(x + L)} y2={round(y)} />
      <path className="floor-plan__axis-head floor-plan__axis--x" d={head(x + L, y, 1, 0)} />
      <text className="floor-plan__axis-label floor-plan__axis--x" x={round(x + L + 8)} y={round(y)}>
        X
      </text>
      <line className="floor-plan__axis floor-plan__axis--y" x1={round(x)} y1={round(y)} x2={round(x)} y2={round(y - L)} />
      <path className="floor-plan__axis-head floor-plan__axis--y" d={head(x, y - L, 0, -1)} />
      <text className="floor-plan__axis-label floor-plan__axis--y" x={round(x)} y={round(y - L - 8)}>
        Y
      </text>
      <circle className="floor-plan__axis-z" cx={round(x)} cy={round(y)} r={4.5} />
      <circle className="floor-plan__axis-z-dot" cx={round(x)} cy={round(y)} r={1.5} />
      <text className="floor-plan__axis-label floor-plan__axis--z" x={round(x - 9)} y={round(y + 9)}>
        Z
      </text>
    </g>
  );
}

// ------------------------------------------------ small geometry, view px ----

export function midpoint(a: Vec, b: Vec): { x: number; y: number } {
  return { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
}

/** Whichever perpendicular of `u` points away from `centre`. */
export function outwardNormal(u: Vec, from: Vec, centre: Vec): { x: number; y: number } {
  const n = { x: -u.y, y: u.x };
  const away = { x: from.x - centre.x, y: from.y - centre.y };
  return n.x * away.x + n.y * away.y >= 0 ? n : { x: -n.x, y: -n.y };
}

function inwardNormal(u: Vec, from: Vec, centre: Vec): { x: number; y: number } {
  const out = outwardNormal(u, from, centre);
  return { x: -out.x, y: -out.y };
}

/**
 * A run's angle in degrees, folded into [-90, 90) so text never renders upside
 * down — and so a vertical figure reads from the right, bottom to top, which is
 * the drawing convention (read from the bottom or the right of the sheet).
 */
export function uprightAngle(u: Vec): number {
  let deg = (Math.atan2(u.y, u.x) * 180) / Math.PI;
  if (deg >= 90 - 1e-9) deg -= 180;
  if (deg < -90 - 1e-9) deg += 180;
  return deg;
}

/** Two decimals is finer than any display resolves and keeps the DOM small. */
export function round(value: number): number {
  return Number.isFinite(value) ? Math.round(value * 100) / 100 : 0;
}
